import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { IconCheck, IconError, IconInfo, IconWarning, IconClose } from './Icons';

type ToastTone = 'success' | 'error' | 'warning' | 'info';
interface Toast {
  id: number;
  tone: ToastTone;
  message: string;
  title?: string;
}

interface ToastApi {
  push: (message: string, opts?: { tone?: ToastTone; title?: string }) => void;
  success: (message: string, title?: string) => void;
  error: (message: string, title?: string) => void;
  /** Completed, but not fully — never dress this up as success. */
  warning: (message: string, title?: string) => void;
  info: (message: string, title?: string) => void;
}

const ToastCtx = createContext<ToastApi | null>(null);

// eslint-disable-next-line react-refresh/only-export-components
export function useToast(): ToastApi {
  const ctx = useContext(ToastCtx);
  if (!ctx) throw new Error('useToast must be used within <ToastProvider>');
  return ctx;
}

const ICONS: Record<ToastTone, ReactNode> = {
  success: <IconCheck width={18} height={18} />,
  error: <IconError width={18} height={18} />,
  warning: <IconWarning width={18} height={18} />,
  info: <IconInfo width={18} height={18} />,
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const remove = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback<ToastApi['push']>(
    (message, opts) => {
      const id = Date.now() + Math.random();
      const toast: Toast = { id, message, tone: opts?.tone ?? 'info', title: opts?.title };
      setToasts((prev) => [...prev, toast]);
      setTimeout(() => remove(id), 5000);
    },
    [remove],
  );

  const api = useMemo<ToastApi>(
    () => ({
      push,
      success: (m, title) => push(m, { tone: 'success', title }),
      error: (m, title) => push(m, { tone: 'error', title }),
      warning: (m, title) => push(m, { tone: 'warning', title }),
      info: (m, title) => push(m, { tone: 'info', title }),
    }),
    [push],
  );

  return (
    <ToastCtx.Provider value={api}>
      {children}
      <div className="toast-region" aria-live="polite" aria-atomic="false">
        {toasts.map((t) => (
          <div key={t.id} className={`toast toast--${t.tone}`} role="status">
            <span className={`stat__icon`} style={{ color: `var(--${t.tone === 'error' ? 'danger' : t.tone})` }}>
              {ICONS[t.tone]}
            </span>
            <div className="toast__msg">
              {t.title ? <div className="toast__title">{t.title}</div> : null}
              {t.message}
            </div>
            <button className="btn btn--ghost btn--icon" aria-label="Close" onClick={() => remove(t.id)}>
              <IconClose width={16} height={16} />
            </button>
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}
